import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Result, Space, Spin, Tabs, Tag, Typography } from 'antd';
import { InboxOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { userPath } from '../../../shared/config/paths';
import PageHeader from '../../../shared/components/PageHeader';
import { fmtBytes, fmtCount, s3, toastErr } from './common';
import ObjectBrowser from './ObjectBrowser';
import BucketSettings from './BucketSettings';

const { Text } = Typography;

const BucketDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [sp, setSp] = useSearchParams();
    const tab = sp.get('tab') === 'settings' ? 'settings' : 'objects';
    const [bucket, setBucket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [missing, setMissing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await s3.get(`/buckets/${id}`);
            setBucket(d ? { id: Number(id), ...d } : null);
            setMissing(!d);
        } catch (e) {
            if (e?.status === 404) setMissing(true); else toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => { load(); }, [load]);

    const setTab = (k) => {
        const nsp = new URLSearchParams(sp);
        if (k === 'settings') nsp.set('tab', 'settings'); else nsp.delete('tab');
        setSp(nsp, { replace: true });
    };

    if (missing) {
        return <Result status="404" title={t('Bucket not found')}
            extra={<Button type="primary" onClick={() => navigate(userPath('/s3'))}>{t('Back to buckets')}</Button>} />;
    }

    return (
        <div>
            <PageHeader icon={<InboxOutlined />}
                title={<Space size={8} wrap>
                    <span>{bucket?.name || '…'}</span>
                    {bucket?.public_access ? <Tag color="orange" style={{ margin: 0 }}>{t('Public')}</Tag> : null}
                </Space>}
                subtitle={bucket ? (
                    <Space size={12} wrap>
                        <Text type="secondary">{bucket.region_name || bucket.region}</Text>
                        <Text type="secondary">{fmtBytes(bucket.bytes)}</Text>
                        <Text type="secondary">{t('{{count}} objects', { count: fmtCount(bucket.objects) })}</Text>
                        {Number(bucket.unfinished_uploads) > 0 && (
                            <Text type="secondary">{t('{{count}} unfinished uploads', { count: bucket.unfinished_uploads })}</Text>
                        )}
                    </Space>
                ) : null}
                extra={<Button icon={<ReloadOutlined />} onClick={load} loading={loading} />} />

            {!bucket ? (
                <Card><Spin spinning={loading}><div style={{ height: 120 }} /></Spin></Card>
            ) : (
                <Tabs activeKey={tab} onChange={setTab} items={[
                    { key: 'objects', label: t('Objects'), children: <ObjectBrowser bucket={bucket} /> },
                    {
                        key: 'settings', label: t('Settings'),
                        children: <BucketSettings bucket={bucket} onChanged={load} onDeleted={() => navigate(userPath('/s3'))} />,
                    },
                ]} />
            )}
        </div>
    );
};

export default BucketDetail;
